import "core-js/stable";
import "./taskpane.css";
import { PROVIDERS, getProvider } from "../ai/providers.js";
import { runAgent } from "../ai/agent.js";
import { isMutating } from "../excel/tools.js";
import { marked } from "marked";
import DOMPurify from "dompurify";
import {
  loadConversation,
  saveConversation,
  createConversation,
  listConversations,
  getActiveConversationId,
  setActiveConversation,
  updateConversationMeta,
  removeConversation,
} from "../conversations/store.js";
import { exportConversationJSON, exportAllConversations } from "../conversations/export.js";
import { importConversationJSON } from "../conversations/import.js";
import { countTokens, truncateHistory, getContextLimit, getTokenUsage } from "../ai/tokens.js";

/* global Office, OfficeRuntime */

// Configure marked for safe markdown rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

/**
 * Render markdown text to safe HTML.
 */
function renderMarkdown(text) {
  if (!text) return "";
  const rawHtml = marked.parse(text);
  return DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: [
      "p", "br", "hr", "blockquote", "pre", "code",
      "h1", "h2", "h3", "h4", "h5", "h6",
      "ul", "ol", "li", "table", "thead", "tbody", "tr", "th", "td",
      "strong", "em", "del", "a", "img",
      "span", "div", "sup", "sub",
    ],
    ALLOWED_ATTR: ["href", "title", "alt", "src", "class", "target", "rel"],
    ALLOW_DATA_ATTR: false,
  });
}

const STORE_KEY = "sheetai.config";
// The auth gateway lives at the same origin in production (Caddy proxies /api/*
// to the auth-server). In local dev the gateway serves HTTPS on :3001.
const isLocal = location.hostname === "localhost" || location.hostname === "127.0.0.1";
const AUTH_BASE = isLocal ? "https://localhost:3001" : location.origin;

// Endpoint the agent calls for a given provider, via the auth gateway.
function chatBaseUrl(providerId) {
  return `${AUTH_BASE}/api/${providerId}`;
}

// Build the config object the agent needs: base URL points at the gateway, and
// the JWT (authToken) authenticates the user. No provider key is sent.
function agentConfig() {
  return {
    providerId: config.providerId,
    model: config.model || getProvider(config.providerId).defaultModel,
    baseUrl: chatBaseUrl(config.providerId),
    authToken: config.authToken,
  };
}

// config now holds the signed-in session (JWT) + chosen provider/model.
// No API key is stored client-side — the gateway holds the provider key.
let config = { providerId: "alibaba", model: "", authToken: "", email: "", role: "", tokenQuota: 0, tokenUsed: 0 };
let history = [];
let busy = false;
let abortController = null;
let lastUserMessage = null;
let currentConversationId = null;

Office.onReady((info) => {
  if (info.host !== Office.HostType.Excel) {
    document.getElementById("messages").innerHTML =
      '<div class="msg error">This add-in only runs in Excel.</div>';
    return;
  }
  initProviderSelect();
  wireEvents();
  loadConfig();
  loadActiveConversation();
});

function $(id) {
  return document.getElementById(id);
}

function initProviderSelect() {
  const sel = $("provider-select");
  sel.innerHTML = "";
  for (const p of Object.values(PROVIDERS)) {
    const opt = document.createElement("option");
    opt.value = p.id;
    opt.textContent = p.label;
    sel.appendChild(opt);
  }
  sel.addEventListener("change", () => applyProviderDefaults(sel.value));
}

function applyProviderDefaults(id) {
  const p = getProvider(id);
  // Remember the current selection so we can keep it if the new provider offers it.
  const prev = $("model").value;
  const sel = $("model");
  sel.innerHTML = "";
  for (const m of p.models) {
    const o = document.createElement("option");
    o.value = m;
    o.textContent = m;
    sel.appendChild(o);
  }
  // Keep the previous model if this provider has it; otherwise fall back to default.
  sel.value = p.models.includes(prev) ? prev : (p.models.includes(config.model) ? config.model : p.defaultModel);
}

function wireEvents() {
  $("settings-btn").addEventListener("click", () => toggleSettings(true));
  $("close-settings").addEventListener("click", () => toggleSettings(false));
  $("save-settings").addEventListener("click", saveConfig);
  $("logout-btn").addEventListener("click", onLogout);
  $("login-password").addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveConfig();
    }
  });
  // Change-password mini form.
  $("changepw-btn").addEventListener("click", () => {
    $("changepw-form").classList.toggle("hidden");
  });
  $("cp-cancel").addEventListener("click", () => {
    $("changepw-form").classList.add("hidden");
    $("cp-error").textContent = "";
  });
  $("cp-save").addEventListener("click", onChangePassword);
  $("send").addEventListener("click", onSend);
  $("stop").addEventListener("click", onStop);
  $("prompt").addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  });

  // Conversations panel
  $("conversations-btn").addEventListener("click", () => toggleConversations(true));
  $("close-conversations").addEventListener("click", () => toggleConversations(false));
  $("new-conv-btn").addEventListener("click", onNewConversation);
  $("export-all-btn").addEventListener("click", onExportAll);
  $("import-file").addEventListener("change", onImportConversation);
}

function toggleSettings(show) {
  $("settings").classList.toggle("hidden", !show);
  $("chat").classList.toggle("hidden", show);
  $("conversations").classList.add("hidden");
  // Refresh quota each time the panel opens, so usage is up to date.
  if (show && config.authToken) loadProfile();
}

async function loadConfig() {
  try {
    const raw = await OfficeRuntime.storage.getItem(STORE_KEY);
    if (raw) config = { ...config, ...JSON.parse(raw) };
  } catch (e) {
    /* ignore */
  }
  $("provider-select").value = config.providerId;
  // applyProviderDefaults populates the model <select> and selects config.model
  // if this provider offers it (falling back to the provider default).
  applyProviderDefaults(config.providerId);
  if (config.email) $("login-email").value = config.email;
  refreshProviderPill();
  reflectAuthState();
  // Open the sign-in panel automatically if not signed in.
  if (!config.authToken) toggleSettings(true);
}

// Update Settings UI to reflect whether we have a session.
function reflectAuthState() {
  const signedIn = !!config.authToken;
  // Toggle profile card vs. sign-in form.
  $("profile-card").classList.toggle("hidden", !signedIn);
  $("signin-form").classList.toggle("hidden", signedIn);
  // Button visibility.
  $("logout-btn").classList.toggle("hidden", !signedIn);
  $("changepw-btn").classList.toggle("hidden", !signedIn);
  $("save-settings").textContent = signedIn ? "Save" : "Sign in";
  // Always start with the change-password form hidden.
  $("changepw-form").classList.add("hidden");

  if (signedIn) {
    $("profile-email").textContent = config.email || "";
    $("profile-role").textContent = config.role === "admin" ? "Administrator" : "Member";
    $("profile-avatar").textContent = (config.email || "?").trim().charAt(0);
    renderQuota(config.tokenQuota, config.tokenUsed);
  }
}

// Render the token-usage bar from quota numbers (0 quota = unlimited).
function renderQuota(quota, used) {
  used = Number(used || 0);
  quota = Number(quota || 0);
  const fill = $("quota-fill");
  const text = $("quota-text");
  const sub = $("quota-sub");
  if (!quota) {
    text.textContent = `${used.toLocaleString()} tokens`;
    fill.style.width = "0%";
    fill.className = "";
    sub.textContent = "Unlimited quota";
    return;
  }
  const pct = Math.min(100, Math.round((used / quota) * 100));
  text.textContent = `${pct}%`;
  fill.style.width = pct + "%";
  fill.className = pct >= 100 ? "full" : pct >= 80 ? "warn" : "";
  sub.textContent = `${used.toLocaleString()} / ${quota.toLocaleString()} tokens used`;
}

// Fetch fresh profile + quota from the gateway and update config + UI.
async function loadProfile() {
  if (!config.authToken) return;
  try {
    const res = await fetch(`${AUTH_BASE}/api/me`, {
      headers: { Authorization: `Bearer ${config.authToken}` },
    });
    if (res.status === 401) {
      // Session expired — drop it so the user is prompted to sign in again.
      await onLogout();
      return;
    }
    const data = await res.json().catch(() => ({}));
    if (data.user) {
      config = {
        ...config,
        email: data.user.email,
        role: data.user.role,
        tokenQuota: data.user.token_quota,
        tokenUsed: data.user.token_used,
      };
      await persistConfig();
      reflectAuthState();
    }
  } catch (e) {
    /* network hiccup — keep last-known values */
  }
}

// Sign in: exchange email/password for a JWT at the auth gateway, then persist.
async function saveConfig() {
  const providerId = $("provider-select").value;
  const model = $("model").value;
  const errEl = $("login-error");
  errEl.textContent = "";

  // If already signed in, just save provider/model changes.
  if (config.authToken) {
    config = { ...config, providerId, model };
    await persistConfig();
    refreshProviderPill();
    toggleSettings(false);
    return;
  }

  const email = $("login-email").value.trim();
  const password = $("login-password").value;
  if (!email || !password) {
    errEl.textContent = "Enter your email and password.";
    return;
  }
  $("save-settings").disabled = true;
  $("save-settings").textContent = "Signing in…";
  try {
    const res = await fetch(`${AUTH_BASE}/api/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Sign-in failed (${res.status}).`);
    config = {
      providerId,
      model,
      authToken: data.token,
      email: data.user.email,
      role: data.user.role,
      tokenQuota: data.user.token_quota,
      tokenUsed: data.user.token_used,
    };
    $("login-password").value = "";
    await persistConfig();
    refreshProviderPill();
    reflectAuthState();
    // Stay on the profile view so the user sees they're signed in + their quota.
  } catch (e) {
    errEl.textContent = e.message;
  } finally {
    $("save-settings").disabled = false;
    $("save-settings").textContent = config.authToken ? "Save" : "Sign in";
  }
}

async function onLogout() {
  config = { ...config, authToken: "", role: "", tokenQuota: 0, tokenUsed: 0, email: config.email };
  await persistConfig();
  $("login-password").value = "";
  $("login-error").textContent = "";
  refreshProviderPill();
  reflectAuthState();
}

// Change the signed-in user's password via the gateway.
async function onChangePassword() {
  const errEl = $("cp-error");
  errEl.textContent = "";
  const current = $("cp-current").value;
  const next = $("cp-new").value;
  const confirmNext = $("cp-confirm").value;
  if (!current || !next) return (errEl.textContent = "Enter your current and new password.");
  if (next !== confirmNext) return (errEl.textContent = "New passwords do not match.");
  if (next.length < 8) return (errEl.textContent = "New password must be at least 8 characters.");
  $("cp-save").disabled = true;
  try {
    const res = await fetch(`${AUTH_BASE}/api/change-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${config.authToken}` },
      body: JSON.stringify({ currentPassword: current, newPassword: next }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Failed (${res.status}).`);
    $("cp-current").value = $("cp-new").value = $("cp-confirm").value = "";
    $("changepw-form").classList.add("hidden");
    $("login-error").textContent = "Password updated.";
  } catch (e) {
    errEl.textContent = e.message;
  } finally {
    $("cp-save").disabled = false;
  }
}

async function persistConfig() {
  try {
    await OfficeRuntime.storage.setItem(STORE_KEY, JSON.stringify(config));
  } catch (e) {
    /* ignore */
  }
}

/**
 * Load active conversation from storage on startup.
 */
async function loadActiveConversation() {
  try {
    const activeId = await getActiveConversationId();
    if (activeId) {
      const conv = await loadConversation(activeId);
      if (conv) {
        currentConversationId = conv.id;
        history = conv.messages || [];
        // Restore UI messages
        const messagesDiv = $("messages");
        for (const msg of history) {
          if (msg.role === "user") {
            addMessage("user", msg.content || "");
          } else if (msg.role === "assistant" && msg.content) {
            addMessage("assistant", msg.content);
          }
        }
      }
    }
  } catch (e) {
    console.error("Error loading conversation:", e);
  }
}

/**
 * Save current conversation to storage.
 */
async function saveCurrentConversation() {
  try {
    if (!currentConversationId) {
      // Create new conversation
      const firstUserMsg = history.find((m) => m.role === "user");
      const convName = firstUserMsg ? firstUserMsg.content.substring(0, 40) : "Conversation";
      const conv = await createConversation(convName);
      currentConversationId = conv.id;
    }
    // Update conversation with current history
    const conv = await loadConversation(currentConversationId);
    if (conv) {
      conv.messages = history;
      conv.messageCount = history.length;
      conv.updatedAt = new Date().toISOString();
      await saveConversation(conv);
      await updateConversationMeta(conv);
    }
    await updateTokenCounter();
  } catch (e) {
    console.error("Error saving conversation:", e);
  }
}

/**
 * Update token counter display in the header.
 */
async function updateTokenCounter() {
  try {
    const usage = await getTokenUsage(history, config.model);
    const counter = $("token-counter");
    const n = usage.tokens;
    const compact = n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + "k" : String(n);
    counter.textContent = compact;
    counter.title = `${n.toLocaleString()} tokens in this conversation`;
    counter.className = "token-info";
    if (usage.status === "warning") {
      counter.classList.add("token-warn");
    } else if (usage.status === "critical") {
      counter.classList.add("token-critical");
    }
  } catch (e) {
    console.error("Error updating token counter:", e);
  }
}

function refreshProviderPill() {
  const pill = $("active-provider");
  if (config.authToken) {
    const p = getProvider(config.providerId);
    const model = config.model || p.defaultModel;
    pill.textContent = `${p.shortLabel || p.label} · ${model}`;
    pill.title = `${p.label} — ${model}`;
    pill.classList.add("connected");
  } else {
    pill.textContent = "Not signed in";
    pill.title = "";
    pill.classList.remove("connected");
  }
}

function addMessage(role, text) {
  const div = document.createElement("div");
  div.className = `msg ${role}`;
  if (role === "assistant") {
    div.innerHTML = renderMarkdown(text);
  } else {
    div.textContent = text;
  }
  $("messages").appendChild(div);
  scrollDown();
  return div;
}

function addToolLine(parent, text) {
  const t = document.createElement("div");
  t.className = "tool";
  t.textContent = text;
  parent.appendChild(t);
  scrollDown();
}

// Shown after a turn that edited the workbook. Edits are applied directly (no
// per-action confirmation), so this reminds the user they can revert with undo.
function addUndoHint(parent) {
  const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
  const combo = isMac ? "⌘Z" : "Ctrl+Z";
  const t = document.createElement("div");
  t.className = "tool undo-hint";
  t.textContent = `↩ Workbook edited. Press ${combo} in Excel to undo.`;
  parent.appendChild(t);
  scrollDown();
}

function scrollDown() {
  const c = $("chat");
  c.scrollTop = c.scrollHeight;
}

async function onSend() {
  if (busy) return;
  const input = $("prompt");
  const text = input.value.trim();
  if (!text) return;
  if (!config.authToken) {
    addMessage("error", "Please sign in first: open Settings (⚙) and enter your email and password.");
    toggleSettings(true);
    return;
  }
  input.value = "";
  addMessage("user", text);
  lastUserMessage = text;
  busy = true;
  abortController = new AbortController();
  $("send").classList.add("hidden");
  $("stop").classList.remove("hidden");
  $("prompt").disabled = true;

  const thinking = addMessage("assistant", "");
  thinking.classList.add("typing");
  thinking.textContent = "Thinking…";
  let assistantEl = thinking;
  let firstText = true;
  let accumulatedText = "";
  let didEdit = false;

  try {
    history = await runAgent({
      config: agentConfig(),
      history,
      userMessage: text,
      signal: abortController.signal,
      onTextChunk: (chunk) => {
        if (firstText) {
          assistantEl.classList.remove("typing");
          accumulatedText = chunk;
          // Use textContent during streaming for performance
          assistantEl.textContent = accumulatedText;
          firstText = false;
        } else {
          accumulatedText = chunk;
          assistantEl.textContent = accumulatedText;
        }
        scrollDown();
      },
      onAssistantText: (t) => {
        if (firstText) {
          assistantEl.classList.remove("typing");
          assistantEl.innerHTML = renderMarkdown(t);
          firstText = false;
        } else {
          assistantEl = addMessage("assistant", t);
        }
      },
      onToolStart: (name, args) => {
        if (assistantEl.classList.contains("typing")) {
          assistantEl.classList.remove("typing");
          assistantEl.textContent = "";
        }
        addToolLine(assistantEl, `→ ${name}(${shortArgs(args)})`);
      },
      onToolResult: (name, result) => {
        const summary = result.ok
          ? result.csv
            ? `✓ ${name}: ${result.rows ?? ""}×${result.columns ?? ""} read`
            : `✓ ${name}`
          : `✗ ${name}: ${result.error}`;
        addToolLine(assistantEl, summary);
        if (result.ok && isMutating(name)) didEdit = true;
      },
      // No confirmMutation: edits are applied directly (like Claude for Excel).
      // The user reviews the tool log above and can press Ctrl+Z / Cmd+Z to undo.
    });
    if (assistantEl.classList.contains("typing")) {
      assistantEl.textContent = "Done.";
      assistantEl.classList.remove("typing");
    } else if (accumulatedText) {
      // Streaming completed, render final markdown
      assistantEl.innerHTML = renderMarkdown(accumulatedText);
    }
    if (didEdit) addUndoHint(assistantEl);
  } catch (e) {
    assistantEl.classList.remove("typing");
    if (e.name === "AbortError") {
      if (accumulatedText) {
        assistantEl.innerHTML = renderMarkdown(accumulatedText);
      } else {
        assistantEl.textContent = "Stopped.";
      }
      assistantEl.classList.add("typing");
    } else {
      assistantEl.classList.add("error");
      assistantEl.textContent = `Error: ${e.message || e}`;
    }
  } finally {
    busy = false;
    abortController = null;
    $("send").classList.remove("hidden");
    $("stop").classList.add("hidden");
    $("prompt").disabled = false;
    // Save conversation to storage
    await saveCurrentConversation();
    // Refresh token quota in the background (usage changed this turn).
    loadProfile();
  }
}

function onStop() {
  if (abortController) {
    abortController.abort();
  }
}

function shortArgs(args) {
  const s = JSON.stringify(args);
  return s.length > 80 ? s.slice(0, 77) + "…" : s;
}

// ============================================================================
// Conversations Panel
// ============================================================================

function toggleConversations(show) {
  $("conversations").classList.toggle("hidden", !show);
  $("chat").classList.toggle("hidden", show);
  $("settings").classList.add("hidden");
  if (show) {
    loadConversationList();
  }
}

async function loadConversationList() {
  const listEl = $("conv-list");
  const conversations = await listConversations();

  if (conversations.length === 0) {
    listEl.innerHTML = '<div class="conv-empty">No conversations yet</div>';
    return;
  }

  // Sort by updatedAt descending
  conversations.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

  listEl.innerHTML = "";
  for (const conv of conversations) {
    const item = document.createElement("div");
    item.className = "conv-item" + (conv.id === currentConversationId ? " active" : "");
    item.dataset.id = conv.id;

    const date = new Date(conv.updatedAt);
    const dateStr = date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    item.innerHTML = `
      <div class="conv-item-info">
        <div class="conv-item-name">${escapeHtml(conv.name)}</div>
        <div class="conv-item-meta">${conv.messageCount} messages · ${dateStr}</div>
      </div>
      <div class="conv-item-actions">
        <button class="conv-item-btn export" title="Export">📤</button>
        <button class="conv-item-btn delete" title="Delete">🗑️</button>
      </div>
    `;

    // Click to switch conversation
    item.querySelector(".conv-item-info").addEventListener("click", () => switchConversation(conv.id));

    // Export button
    item.querySelector(".export").addEventListener("click", async (e) => {
      e.stopPropagation();
      const c = await loadConversation(conv.id);
      if (c) exportConversationJSON(c);
    });

    // Delete button. window.confirm() is blocked in the Office WebView, so
    // delete directly (the list refreshes immediately, which is the feedback).
    item.querySelector(".delete").addEventListener("click", async (e) => {
      e.stopPropagation();
      await removeConversation(conv.id);
      if (conv.id === currentConversationId) {
        await startNewConversation();
      }
      loadConversationList();
    });

    listEl.appendChild(item);
  }
}

async function switchConversation(id) {
  await saveCurrentConversation();
  const conv = await loadConversation(id);
  if (!conv) return;

  currentConversationId = id;
  history = conv.messages || [];
  await setActiveConversation(id);

  // Update UI
  const messagesDiv = $("messages");
  messagesDiv.innerHTML = '<div class="msg system">Ask me about your workbook. I can read sheets, answer questions, build formulas, and edit cells.</div>';

  for (const msg of history) {
    if (msg.role === "user") {
      addMessage("user", msg.content || "");
    } else if (msg.role === "assistant" && msg.content) {
      addMessage("assistant", msg.content);
    }
  }

  scrollDown();
  toggleConversations(false);
  updateTokenCounter();
}

async function onNewConversation() {
  await startNewConversation();
  toggleConversations(false);
}

async function startNewConversation() {
  await saveCurrentConversation();
  currentConversationId = null;
  history = [];
  lastUserMessage = null;

  const messagesDiv = $("messages");
  messagesDiv.innerHTML = '<div class="msg system">Ask me about your workbook. I can read sheets, answer questions, build formulas, and edit cells.</div>';

  updateTokenCounter();
}

async function onExportAll() {
  const conversations = await listConversations();
  const allConvs = [];
  for (const meta of conversations) {
    const conv = await loadConversation(meta.id);
    if (conv) allConvs.push(conv);
  }
  if (allConvs.length > 0) {
    exportAllConversations(allConvs);
  } else {
    alert("No conversations to export");
  }
}

async function onImportConversation(e) {
  const file = e.target.files[0];
  if (!file) return;

  try {
    await importConversationJSON(file);
    await loadConversationList();
    alert("Conversation imported successfully!");
  } catch (err) {
    alert("Import failed: " + err.message);
  }

  // Reset file input
  e.target.value = "";
}

function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}
